import React, { FormEvent, useState } from 'react';
import {
  IonRow,
  IonCol,
  IonGrid,
  IonPage,
  IonContent,
  IonButton,
  IonAlert,
  IonIcon
} from '@ionic/react';
import { connect } from 'react-redux';
import AppHeader from '../../components/AppHeader';
import { useHistory } from 'react-router';
import { resetAppointment, setSidep } from '../../store/actions/appointments';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import './HasAppointment.scss';
import { AuthenticationRootState } from '../../store/reducers/authentication';
import { CheckingStepProps } from '../../navigators/CheckinNavigator';
import StepperCheckin from '../../components/StepperCheckin';
import { Laboratory } from '../../models';
import { logInOutline } from 'ionicons/icons';

type HasAppointmentProps = {
  trueNextStepRouterLink: string;
  falseNextStepRouterLink: string;
  laboratories: Laboratory[];
  checkinMessage?: string;
};

const mapStateToProps = (
  state: {
    authentication: AuthenticationRootState;
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    sidep: state.appointments.sidep,
    isLoggedIn: !!state.authentication.accessToken,
    patientToUpdate:
      state.appointments.appointment.patient ||
      state.authentication.user?.patient
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  setSidep: ({ key, value }: { key: string; value: unknown }) =>
    dispatch(setSidep(key, value)),
  resetAppointment: () => dispatch(resetAppointment())
});

const HasAppointment: React.FC<
  ReturnType<typeof mapDispatchToProps> &
    HasAppointmentProps &
    CheckingStepProps &
    ReturnType<typeof mapStateToProps>
> = ({
  step,
  trueNextStepRouterLink = '/',
  falseNextStepRouterLink = '/',
  laboratories,
  checkinMessage
}) => {
  const history = useHistory();
  const [showNoAppointmentAlert, setShowNoAppointmentAlert] = useState(false);

  function onFormSubmit(e: FormEvent) {
    e.preventDefault();
  }

  function submit(hasAppointment: boolean) {
    history.push(
      hasAppointment ? trueNextStepRouterLink : falseNextStepRouterLink
    );
  }

  function getIntro() {
    if (laboratories.length === 1) {
      return (
        <>
          {checkinMessage ? (
            <p className="home-card__intro ion-margin-bottom">
              {checkinMessage}
            </p>
          ) : null}
          {laboratories[0].checkin_welcome_message ? (
            <div
              dangerouslySetInnerHTML={{
                __html: laboratories[0].checkin_welcome_message
              }}
              className="home-card__intro ion-margin-bottom"
            />
          ) : null}
          {laboratories[0].appointment_type === 'all' ? (
            <p className="home-card__intro">
              Avez-vous préalablement reçu une confirmation de rendez-vous suite
              à un préenregistrement sur le Pass Unilabs&nbsp;?
            </p>
          ) : null}
        </>
      );
    } else {
      return (
        <>
          {checkinMessage ? (
            <p className="home-card__intro ion-margin-bottom">
              {checkinMessage}
            </p>
          ) : null}
        </>
      );
    }
  }

  function getSingleLaboratoryButtons(laboratory: Laboratory) {
    return (
      <>
        {laboratory.appointment_type === 'appointment_only' ||
        laboratory.appointment_type === 'all' ? (
          <IonCol size={laboratory.appointment_type === 'all' ? '6' : '12'}>
            <IonButton
              expand="block"
              size="large"
              color="primary"
              class={`large ${
                laboratory?.appointment_type !== 'appointment_only' &&
                laboratory?.appointment_type !== 'all' &&
                'button-custom-disabled'
              }`}
              routerDirection="forward"
              onClick={() => {
                if (
                  laboratory?.appointment_type === 'appointment_only' ||
                  laboratory.appointment_type === 'all'
                ) {
                  submit(true);
                }
              }}
              className="button-auto-height button-full-height"
            >
              {laboratory.appointment_type === 'all' ? (
                <div>
                  Oui,
                  <br />
                  <small>
                    j’ai reçu par email une confirmation de prise en charge
                  </small>
                </div>
              ) : (
                <div className="flex align-center">
                  <div>Signaler ma présence en laboratoire</div>
                  <IonIcon
                    icon={logInOutline}
                    className="ion-margin-start"
                    size="large"
                  />
                </div>
              )}
            </IonButton>
          </IonCol>
        ) : null}
        {laboratory.appointment_type === 'direct_only' ||
        laboratory.appointment_type === 'all' ? (
          <IonCol size={laboratory.appointment_type === 'all' ? '6' : '12'}>
            <IonButton
              expand="block"
              size="large"
              color="primary"
              class={`large ${
                laboratory?.appointment_type !== 'direct_only' &&
                laboratory?.appointment_type !== 'all' &&
                'button-custom-disabled'
              }`}
              routerDirection="forward"
              onClick={() => {
                if (
                  laboratory?.appointment_type === 'direct_only' ||
                  laboratory.appointment_type === 'all'
                ) {
                  submit(false);
                } else {
                  setShowNoAppointmentAlert(true);
                }
              }}
              className="button-auto-height button-full-height"
            >
              {laboratory.appointment_type === 'all' ? (
                <div>
                  Non,
                  <br />
                  <small>
                    je souhaite me préenregistrer et annoncer mon arrivée au
                    laboratoire
                  </small>
                </div>
              ) : (
                <div className="flex align-center">
                  <div>Me pré-enregistrer</div>
                  <IonIcon
                    icon={logInOutline}
                    className="ion-margin-start"
                    size="large"
                  />
                </div>
              )}
            </IonButton>
            <IonAlert
              isOpen={showNoAppointmentAlert}
              onDidDismiss={() => setShowNoAppointmentAlert(false)}
              header={`Vous n'avez pas de rendez-vous ?`}
              message={`Notre personnel d'accueil va vous aider.`}
              buttons={[`J'ai compris`]}
            />
          </IonCol>
        ) : null}
      </>
    );
  }

  function getMultipleLaboratoriesButtons() {
    return (
      <IonCol size="12">
        <IonButton
          expand="block"
          size="large"
          color="primary"
          class="large"
          routerDirection="forward"
          onClick={() => {
            submit(true);
          }}
          className="button-auto-height button-full-height"
        >
          <div className="flex align-center">
            <div>Signaler ma présence en laboratoire</div>
            <IonIcon
              icon={logInOutline}
              className="ion-margin-start"
              size="large"
            />
          </div>
        </IonButton>
      </IonCol>
    );
  }

  return (
    <IonPage className="p-has-appointment">
      <AppHeader />
      <StepperCheckin step={step} />
      <IonContent>
        <div className="wrapper wrapper--medium wrapper--stretch wrapper--centered-md ion-margin-top">
          <div className="home-card">
            <header className="ion-margin-bottom ion-margin-start ion-margin-end">
              <div className="home-card__header__title ion-margin-bottom">
                <h1>Bienvenue sur</h1>
                <img
                  src={require('../../assets/images/logo-unilabs-le-pass.svg')}
                  alt="Le Pass"
                />
              </div>
              {getIntro()}
            </header>
            <form onSubmit={onFormSubmit}>
              <IonGrid>
                <IonRow>
                  <IonCol>
                    <div
                      className="wrapper wrapper--large ion-margin-start ion-margin-end"
                      style={{ position: 'relative' }}
                    >
                      <IonGrid>
                        <IonRow class="ion-justify-content-start">
                          {laboratories.length === 1
                            ? getSingleLaboratoryButtons(laboratories[0])
                            : getMultipleLaboratoriesButtons()}
                        </IonRow>
                      </IonGrid>
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </form>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(HasAppointment);
